import FirestoreClient from '../clients/FirestoreClient.js';
import { PLACEHOLDERS, getProductTypeColor } from '../constants/index.js';
import { detectRegion } from '../utils/regionDetection.js';
import { normalizeProductTypes, normalizeImages } from '../utils/normalization.js';
import { withTimeout } from '../utils/withTimeout.js';
import { trackCarouselNavigation } from '../services/AnalyticsService.js';
import ko from 'knockout';

class ProductViewModel {
    constructor(currentLangObservable) {
        this.currentLang = currentLangObservable;

        this.displayCards = ko.observableArray([]);
        this.currentProduct = ko.observable(null);
        this.currentImageIndex = ko.observable(0);
        
        // Issue 2.1: Promise-based carousel transition queue
        // Ensures sequential transitions with guaranteed completion
        // Replaces fragile boolean lock flag with Promise chain
        // (Declared as class field, not constructor assignment)

        // Phase 2: Product Family Filter
        this.selectedProductType = ko.observable('mobile');
        this.availableProductTypes = ko.observableArray([]);
        
        // Show/hide idea blog suggestion card
        this.showSuggestionCard = ko.observable(false);
        
        // Issue 1.1: Type index for O(1) filtering performance
        // Maps product type → array of cards with that type
        // Example: { 'mobile': [card1, card2], 'web': [card3, card4] }
        this.typeIndex = {};
        
        // Issue 1.2: Loading state for products list
        // Shows spinner while Firestore query is in progress
        this.isLoadingCards = ko.observable(false);
        
        // Issue 1.4: Error recovery state
        // Stores error message and retry function when card load fails
        this.cardLoadError = ko.observable(null);
        
        // Phase 1: Computed array: filter display cards by selected product type
        // Issue 1.1: Use typeIndex for O(1) performance instead of O(n²) filtering
        this.filteredDisplayCards = ko.computed(() => {
            const selectedType = this.selectedProductType();
            const showSuggestion = this.showSuggestionCard();
            
            // Issue 1.1: O(1) lookup using type index instead of array.filter()
            const productCards = this.typeIndex[selectedType] || [];
            
            // Find suggestion card from display cards
            const suggestionCard = this.displayCards().find(c => c.cardType === 'suggestionLink');
            
            // Include suggestion card only if toggle is enabled
            return (showSuggestion && suggestionCard) ? [...productCards, suggestionCard] : productCards;
        });

        // Subscribe to language changes to reload display cards
        this.currentLang.subscribe(() => {
            this.loadDisplayCards();
        });
    }

    /**
     * Issue 1.3: Get current region using shared utility
     * Unified region detection across ViewModels
     * @returns {string} Region code: 'pl', 'eu', or 'en'
     */
    #getCurrentRegion() {
        return detectRegion();
    }

    /**
     * Get border style for product type navbar
     * Uses centralized color constants from UI_COLORS
     * @param {string} productType - 'mobile', 'web', or 'desktop'
     * @returns {string} CSS border style or 'none'
     */
    getProductTypeBorder = (productType) => {
        const isSelected = this.selectedProductType() === productType;
        if (!isSelected) return 'none';
        const colors = getProductTypeColor(productType);
        return colors.border;
    };

    /**
     * Get background color for product detail header
     * Uses the light tint of the selected category (what user is viewing)
     * @returns {string} CSS background color hex code
     */
    getProductDetailBgColor = () => {
        const selectedType = this.selectedProductType();
        if (!selectedType) return '#ffffff';
        const colors = getProductTypeColor(selectedType);
        return colors.bgLight || '#ffffff';
    };

    /**
     * Issue 2.1: Promise queue for carousel transitions
     * Prevents race conditions from multiple concurrent transitions
     */
    #carouselTransitionQueue = Promise.resolve();

    /**
     * Issue 2.4: Preload cache for carousel images
     * Stores Image objects mapped by index for fast display
     * Prevents loading delays when user clicks next/previous images
     */
    #preloadedImages = new Map();

    /**
     * Issue 2.4: Preload image sequence to avoid loading delays
     * Creates Image objects and caches them for quick display
     * Runs in background without blocking UI
     * @param {Array} images - Array of image objects with url property
     * @param {Array<number>} indices - Indices to preload (default: first 3)
     * @private
     */
    #preloadImageSequence(images, indices) {
        if (!Array.isArray(images) || images.length === 0) return;
        
        const indexesToPreload = indices || [0, 1, 2].filter(i => i < images.length);
        
        indexesToPreload.forEach(idx => {
            if (this.#preloadedImages.has(idx)) return; // Already preloaded
            
            const imgObj = images[idx];
            if (!imgObj || !imgObj.url) return;
            
            const preloadImg = new Image();
            preloadImg.src = imgObj.url;
            this.#preloadedImages.set(idx, preloadImg);
        });
    }
    
    /**
     * Issue 2.1: Perform image transition with Promise-based sequencing
     * Also handles Issue 2.4: Preload next image during transition
     * Replaces fragile timeout-based locking with Promise chain
     * Guarantees sequential transitions and automatic cleanup on error
     * @param {number} targetIdx - Index of image to transition to
     * @returns {Promise<void>} Resolves when transition animates are complete
     * @private
     */
    async #performImageTransition(targetIdx) {
        return new Promise((resolve) => {
            const imgEl = document.querySelector('#main-content img.mx-auto');
            
            // Issue 2.4: Start preloading next image while animating
            const product = this.currentProduct();
            if (product && Array.isArray(product.images)) {
                const nextIdx = (targetIdx + 1) % product.images.length;
                if (!this.#preloadedImages.has(nextIdx)) {
                    this.#preloadImageSequence(product.images, [nextIdx]);
                }
            }
            
            // Fade out current image
            if (imgEl) {
                imgEl.classList.add('opacity-0', 'transition-opacity', 'duration-300');
            }
            
            // Wait for fade-out, then swap image
            setTimeout(() => {
                this.currentImageIndex(targetIdx);
                
                // Fade in new image
                if (imgEl) {
                    setTimeout(() => {
                        imgEl.classList.remove('opacity-0');
                    }, 30);
                }
                
                // Resolve after full animation (fade-out time + fade-in + buffer)
                setTimeout(resolve, 370);
            }, 350);
        });
    }

    /**
     * Issue 2.1: Queue image transition to ensure sequential execution
     * Prevents race conditions from rapid user clicks
     * @param {number} targetIdx - Index of image to navigate to
     */
    goToImage = (targetIdx) => {
        const product = this.currentProduct();
        if (!this.#isValidImageTarget(product, targetIdx)) return;
        
        // Track carousel navigation
        const direction = targetIdx > this.currentImageIndex() ? 'next' : 'prev';
        trackCarouselNavigation(direction, targetIdx, product.id);
        
        // Chain this transition after the previous one completes
        // This prevents overlapping transitions and race conditions
        this.#carouselTransitionQueue = this.#carouselTransitionQueue
            .then(() => this.#performImageTransition(targetIdx))
            .catch((error) => {
                // Log but don't throw - ensures queue continues even if transition fails
                console.error('[ProductViewModel] Carousel transition error:', error);
            });
    };

    #isValidImageTarget(product, targetIdx) {
        if (!product || !Array.isArray(product.images) || product.images.length === 0) return false;
        if (targetIdx < 0 || targetIdx >= product.images.length) return false;
        if (targetIdx === this.currentImageIndex()) return false;
        return true;
    }

    nextImage = () => {
        const product = this.currentProduct();
        if (!product || !Array.isArray(product.images) || product.images.length === 0) return;
        let nextIdx = this.currentImageIndex() + 1;
        if (nextIdx >= product.images.length) nextIdx = 0;
        this.goToImage(nextIdx);
    };

    /**
     * Extract version status from versions array by matching current version number
     * @param {Object} versionData - Version object with current number
     * @param {Array} versionsArray - Array of version objects with status
     * @returns {string} Status from matched version
     * @private
     */
    #getVersionStatus(versionData, versionsArray) {
        if (!versionData || !versionData.current || !Array.isArray(versionsArray)) {
            return null;
        }
        const matchingVersion = versionsArray.find(v => v.number === versionData.current);
        return matchingVersion?.status;
    }

    /**
     * Phase 3: Get version badge configuration based on status
     * @param {string} versionStatus - Version status from product data
     * @returns {Object} Badge config with icon, color, and label
     */
    #getVersionBadge(versionStatus) {
        const badges = {
            'released': { icon: '✓', color: 'bg-green-100 text-green-800', label: 'Released' },
            'beta': { icon: 'β', color: 'bg-blue-100 text-blue-800', label: 'Beta' },
            'pre-release': { icon: '→', color: 'bg-yellow-100 text-yellow-800', label: 'Coming Soon' },
            'deprecated': { icon: '✕', color: 'bg-gray-100 text-gray-800', label: 'Legacy' }
        };
        if (!versionStatus) {
            return null;
        }
        return badges[versionStatus] || badges['released'];
    }

    #buildDisplayCard(productData, lang) {
        let displayName = productData.name;
        let displayThumbnail = productData.thumbnailImage || PLACEHOLDERS.PRODUCT_THUMBNAIL_SMALL;
        if (productData.content && productData.content[lang]) {
            displayName = productData.content[lang].name || displayName;
        }
        const versionStatus = this.#getVersionStatus(productData.version, productData.versions);
        const productSlug = productData.slug || productData.id;
        const productTypes = normalizeProductTypes(productData);  // Issue 2.2: Use shared utility
        return {
            cardType: 'product',
            displayName: displayName,
            imageSrc: displayThumbnail,
            targetHash: `product/${productSlug}`,
            productSlug: productSlug,
            productTypes: productTypes,  // Phase 1: Use normalized types array
            versionStatus: versionStatus,
            versionBadge: this.#getVersionBadge(versionStatus),
            hasGuidebook: true  // Phase 5: All products have guides (can be made configurable later)
        };
    }

    /**
     * Phase 1: Extract unique product types from products (handles array format)
     * Flattens array of type arrays into single array of unique types
     * @param {Array} products - Array of product documents
     * @returns {Array} Sorted array of unique product types
     */
    #extractProductTypes(products) {
        // Flatten all product types into single array, handling both old and new formats
        const allTypes = products.flatMap(p => {
            const types = normalizeProductTypes(p);
            return types && types.length > 0 ? types : [];
        });
        const uniqueTypes = [...new Set(allTypes)];
        return uniqueTypes.sort();
    }

    #buildSuggestionCard(lang) {
        return {
            cardType: 'suggestionLink',
            displayName: window.Engryv.i18n.t('products.suggestNext'),
            imageSrc: "images/ideas/Engryv_ideas_small.png",
            targetHash: "next-idea-blog",
            description: window.Engryv.i18n.t('products.suggestNextDesc'),
            versionBadge: null,
            hasGuidebook: false
        };
    }

    /**
     * Issue 1.1: Build index for fast product type filtering
     * Creates map: productType → array of cards with that type
     * Enables O(1) lookups in filteredDisplayCards computed observable
     * @param {Array} cards - Array of display cards
     * @private
     */
    #buildTypeIndex(cards) {
        const index = {};
        cards.forEach(c => {
            // Skip non-product cards (suggestion card, etc)
            if (c.cardType === 'suggestionLink' || !Array.isArray(c.productTypes)) {
                return;
            }
            // Add this card to index for each type it supports
            c.productTypes.forEach(type => {
                if (!index[type]) {
                    index[type] = [];
                }
                index[type].push(c);
            });
        });
        return index;
    }

    loadDisplayCards = async () => {
        let cards = [];
        const lang = this.currentLang();
        const region = this.#getCurrentRegion();
        
        // Issue 1.2: Set loading state before query
        this.isLoadingCards(true);
        // Issue 1.4: Clear any previous error
        this.cardLoadError(null);
        
        try {
            // Issue 4.1: Wrap Firestore query with timeout protection
            const products = await withTimeout(
                FirestoreClient.queryCollection("products", {
                    whereField: "isActive",
                    whereOp: "==",
                    whereValue: true,
                    orderByField: "displayOrder",
                    order: "asc"
                }),
                10000,
                'Product list loading'
            );
            
            // Filter by region: only show products available in current region
            const visibleProducts = products.filter(p => {
                const hasRegion = p.regions && Array.isArray(p.regions) && p.regions.includes(region);
                return hasRegion;
            });
            
            // Phase 2: Extract available product types and set default if needed
            const availableTypes = this.#extractProductTypes(visibleProducts);
            this.availableProductTypes(availableTypes);
            
            // Reset product type selection if the current selection is not available
            if (!availableTypes.includes(this.selectedProductType())) {
                const defaultType = availableTypes[0] || 'mobile';
                this.selectedProductType(defaultType);
            }
            
            for (const productData of visibleProducts) {
                cards.push(this.#buildDisplayCard(productData, lang));
            }
            cards.push(this.#buildSuggestionCard(lang));
            
            // Issue 1.1: Build type index for fast filtering
            this.typeIndex = this.#buildTypeIndex(cards);
            
            this.displayCards(cards);
        } catch (error) {
            console.error('[ProductViewModel] Error loading display cards:', error);
            this.typeIndex = {}; // Reset index on error
            this.displayCards([]);
            // Issue 1.4: Show error message with retry button
            // Issue 4.1: Timeout errors get descriptive message
            this.cardLoadError({
                message: error.message || 'Failed to load products. Please check your connection.',
                retry: () => this.loadDisplayCards()
            });
        } finally {
            // Issue 1.2: Clear loading state when complete (success or error)
            this.isLoadingCards(false);
        }
    };

    /**
     * Get what's currently being loaded for display in loading message
     * Returns the current product name if viewing product detail, or 'products' for main list
     * Provides context-aware feedback about what's loading
     * 
     * @returns {string} Context description for loading message
     */
    getCurrentLoadingContext() {
        const product = this.currentProduct();
        if (product && product.displayName) {
            return product.displayName;
        }
        return 'products';
    }

    #getLangDocData(productData, lang) {
        if (productData.content && productData.content[lang]) {
            return productData.content[lang];
        } else if (lang !== 'en' && productData.content && productData.content['en']) {
            return productData.content['en'];
        }
        return null;
    }

    #buildProduct(productData, langDocData) {
        const images = normalizeImages(langDocData.images);  // Issue 2.2: Use shared utility
        return {
            id: productData.id,
            slug: langDocData.slug || productData.slug,
            price: langDocData.price,
            currency: langDocData.currency,
            versionStatus: this.#getVersionStatus(productData.version, productData.versions),
            viewType: langDocData.viewType || productData.viewType || 'productDetail',
            name: langDocData.name || productData.name || window.Engryv.i18n.t('productDetail.productNameMissing'),
            fullDescription: langDocData.fullDescription || window.Engryv.i18n.t('productDetail.noDescription'),
            thumbnailImage: (images[0] && images[0].url) || productData.thumbnailImage || PLACEHOLDERS.PRODUCT_THUMBNAIL_LARGE,
            images: images,
            creditsInfo: langDocData.creditsInfo,
            availableInfo: langDocData.availableInfo,
            productType: productData.productType
        };
    }

    loadProductDetail = async (productSlugForQuery) => {
        try {
            const lang = (this.currentLang() || 'en').trim();
            // Issue 4.1: Wrap Firestore query with timeout protection
            const productData = await withTimeout(
                FirestoreClient.findDocumentByField('products', 'slug', productSlugForQuery),
                10000,
                'Product detail loading'
            );
            if (!productData) return this.#resetProduct();
            const langDocData = this.#getLangDocData(productData, lang);
            const product = this.#buildProduct(productData, langDocData);
            this.currentProduct(product);
            this.currentImageIndex(0);
            
            // Issue 2.4: Preload first 3 images on detail load
            // Smooth carousel experience without loading delays
            if (product && Array.isArray(product.images) && product.images.length > 0) {
                this.#preloadImageSequence(product.images);
            }
        } catch (e) {
            this.#resetProduct();
        }
    };

    #resetProduct() {
        this.currentProduct(null);
        this.currentImageIndex(0);
        this.typeIndex = {}; // Clear index when resetting
        this.#preloadedImages.clear(); // Issue 2.4: Clear preloaded images
    }
}

export default ProductViewModel;
